import type {
  ConfirmSaveGoalRequest,
  GoalMetricDraftInput,
} from "@features/api/ChatApi";
import type {
  SaveGoalArgs,
  SaveGoalNextStep,
} from "@components/Chat/InteractiveTool/types";
import { getSaveGoalDraftIdFromStorage } from "./saveGoalDraftIdStorage";

const isGoalMetricDraftInput = (
  value: unknown,
): value is GoalMetricDraftInput => {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as GoalMetricDraftInput;
  return (
    typeof candidate.metric_key === "string" &&
    candidate.target_config != null &&
    typeof candidate.target_config === "object"
  );
};

/** 将 save_goal args.metrics（对象数组或 JSON 字符串）规范为 API 草稿列表 */
export const parseSaveGoalMetrics = (
  metrics: unknown,
): GoalMetricDraftInput[] => {
  if (metrics == null) {
    return [];
  }

  let parsed: unknown = metrics;
  if (typeof metrics === "string") {
    try {
      parsed = JSON.parse(metrics);
    } catch {
      return [];
    }
  }

  if (!Array.isArray(parsed)) {
    return [];
  }

  return parsed.filter(isGoalMetricDraftInput);
};

const formatLegacyNextSteps = (
  nextSteps: SaveGoalNextStep[] | undefined,
): string | undefined => {
  if (!nextSteps?.length) {
    return undefined;
  }

  return nextSteps
    .map((step) => `- [ ] ${step.title}: ${step.content}`)
    .join("\n");
};

/**
 * Maps save_goal card args to the POST /goals/confirm-save request body.
 *
 * API fields are passed through while legacy nextSteps is normalized to the
 * current steps markdown field.
 */
export const mapSaveGoalArgsToConfirmSaveRequest = (
  args: SaveGoalArgs,
): ConfirmSaveGoalRequest => {
  const metrics = parseSaveGoalMetrics(args.metrics);
  if (metrics.length === 0) {
    throw new Error(
      "SaveGoal confirm-save requires at least one metric in args.metrics",
    );
  }

  const { nextSteps, steps, goalId: argsGoalId, ...restArgs } = args;
  // 不传 goalId → API 创建分支（新 ACTIVE）；有值 → INACTIVE 草稿确认分支
  const goalId =
    (getSaveGoalDraftIdFromStorage() ?? argsGoalId)?.trim() || undefined;

  return {
    ...restArgs,
    steps: steps ?? formatLegacyNextSteps(nextSteps),
    metrics,
    ...(goalId ? { goalId } : {}),
  };
};
